// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from "@angular/material/button";
import { defer, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { Analysis } from '../../../interfaces/analysis';
import { RefGeneListData, makeColumns, ColumnDef } from '../../../interfaces/hce/hce-ref-gene-list-view';
import { HceService } from '../../../services/hce/hce.service';
import { MessageModalService } from '../../../services/message-modal/message-modal.service';
import { InputComponent } from '../../../shared/input/input.component';
import { SvResponse } from '../../../interfaces/hce/hce-sv';
import { HceAlignmentViewRequest } from "../../../interfaces/hce/hce-alignment-view";

@Component({
  selector: 'app-hce-ref-gene-list-view',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    InputComponent,
  ],
  templateUrl: './hce-ref-gene-list-view.component.html',
  styleUrls: ['./hce-ref-gene-list-view.component.css'],
})
export class HceRefGeneListViewComponent implements AfterViewInit, OnChanges {
  private _sort?: MatSort;
  private _paginator?: MatPaginator;

  @Input() analysis!: Analysis;
  @Input() sv!: SvResponse;
  @Input() refNameMap!: Record<string, string>;
  @Input() filtered!: boolean;
  @Input() selectedAnnotationFiles: string[] = [];
  @ViewChild(MatSort) set matSort(s: MatSort | undefined) {
    if (s) {
      this._sort = s;
      this.dataSource.sort = s;
    }
  }
  @ViewChild(MatPaginator) set matPaginator(p: MatPaginator | undefined) {
    if (p) {
      this._paginator = p;
      this.dataSource.paginator = p;
    }
  }
  
  originalRows: RefGeneListData[] = [];
  dataSource = new MatTableDataSource<RefGeneListData>([]);
  Variants = 0;
  db = "";
  trackByNumber = (_: number, n: number) => n;
  placeHolderText = "Search (Gene Name)";
  textInputType = "Text";
  iconName = "Search";
  searchCtrl = new FormControl('');
  nf = new Intl.NumberFormat();
  loading = true;
  hasError = false;
  paginatorReady = false;

  constructor(
    private hceService: HceService,
    private messageModal: MessageModalService,
  ) {}

  private showLoadFailed(): void {
    this.messageModal.openModal(
      $localize`Error`,
      $localize`Failed to load reference gene list.`
    );
  }

  /** Invoke applyFilter() when either `sv` or `filtered` changes. */
  ngOnChanges(changes: SimpleChanges) {
    if (
      this.originalRows.length && 
      Object.keys(changes).some(
        (change) => change == "sv" || change == "filtered")
    ) {
      this.applyFilter();
    }
  }

  ngAfterViewInit(): void {
    if (this._sort) this.dataSource.sort = this._sort;
    if (this._paginator) {
      this._paginator._intl.itemsPerPageLabel = $localize`Rows per page:`;
      this.dataSource.paginator = this._paginator;
    }

    queueMicrotask(() => {
      this.vm$.subscribe({
        next: () => {
          this.loading = false;
        },
        error: (err) => {
          console.error(err);
          this.loading = false;
          this.hasError = true;
          this.showLoadFailed();
        }
      });
    });
    
    this.dataSource.sortingDataAccessor = (row: RefGeneListData, columnId: string) => {
      switch (columnId) {
        case 'svs':  return row.svs?.join('').length ?? 0;
        case 'chr':
          const c = this.chrTokenToNum(row.chr) ?? 99;
          const s = row.start ?? 0;
          const e = row.end ?? 0;
          return `${c.toString().padStart(2,'0')}${s.toString().padStart(9,'0')}${e.toString().padStart(9,'0')}`;
        case 'pn':   return row.pn === '-' ? 0 : row.pn === '+' ? 1 : 2;
        case 'start':
        case 'end':  return (row as any)[columnId] ?? 0;
        default: {
          const v = (row as any)[columnId];
          return typeof v === 'string' ? v.toLowerCase() : v ?? '';
        }
      }
    };

    // Internal filter for Gene Name (search bar).
    this.dataSource.filterPredicate = (data: RefGeneListData, filter: string) => {
      const q = (filter || '').trim().toLowerCase();
      if (!q) return true;
      const name = (data.gene_name ?? '').toString().toLowerCase();
      return name.includes(q);
    };

    /** Invoke applyFilter when the search bar value changes. */
    this.searchCtrl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe(() => this.applyFilter());
  }

  /** Load the RefGene data (executed only once). */
  vm$ = defer(() => {
    if (!this.analysis?.AnalysisID || !this.analysis?.PanelID) {
      throw new Error('AnalysisID or PanelID is invalid.');
    }

    return forkJoin({
      refgene: this.hceService.getEnhancedRefGene(this.analysis.AnalysisID),
      db: this.hceService.getUcscRefVersion(this.analysis.PanelID),
    }).pipe(
      map(({ refgene, db }) => {
        if (!refgene || !db) {
          throw new Error('Failed to load refgene or db');
        }

        const rows = refgene
          .split(/\r?\n/)
          .map(line => line.trim())
          .filter(line => line.length > 0)
          .map(line => {
            const cols = line.split('\t');

            if (cols.length < 8) return null;

            const toNumberArraySafe = (s: string) =>s.split(',').map(x => Number(x.trim())).filter(n => !Number.isNaN(n));

            const gene_id   = cols[0];
            const chr       = this.refNameMap[cols[1]] ?? '-';
            const pn        = cols[2];
            const start     = parseInt(cols[3]);
            const end       = parseInt(cols[4]);
            const gene_type = cols[6];
            const gene_name = cols[7];
            const svs = cols.length == 9 ? toNumberArraySafe(cols[8]) : [];

            return {
              gene_id,
              chr,
              pn,
              start,
              end,
              gene_type,
              gene_name,
              svs, 
            } as RefGeneListData;
          })
          .filter((r): r is RefGeneListData => !!r);

        return { rows, db };
      }),
      tap(result => {
        const sorted = [...result.rows].sort((a, b) => this.compareChrStartEnd(a, b));
        this.originalRows = sorted;
        this.dataSource.data = sorted;
        this.Variants = sorted.length;
        this.db = result.db;

        // Apply initial filters (reflecting sv/filter settings). 
        this.applyFilter();
      }),
      shareReplay(1)
    );
  });

  /** Type definition for table columns 
   * (key, header label, and cell value accessor function) */
  columns: ColumnDef<RefGeneListData> = makeColumns<RefGeneListData>()({
    gene_id:   { header: 'ID',        cell: r => r.gene_id },
    chr:       { header: 'Chr',       cell: r => r.chr },
    pn:        { header: 'PN',        cell: r => r.pn },
    start:     { header: 'Start',     cell: r => r.start },
    end:       { header: 'End',       cell: r => r.end },
    gene_type: { header: 'Gene Type', cell: r => r.gene_type },
    gene_name: { header: 'Gene Name', cell: r => r.gene_name },
    svs:       { header: 'Svs',       cell: r => r.svs },
  });
  displayedColumns = Object.keys(this.columns);

  private applyFilter() {
    if (!this.originalRows.length) return;

    let rows = this.originalRows;

    if (this.filtered) {
      const svIds = new Set(Object.keys(this.sv.svs).map(k => Number(k)));
      rows = rows.filter(row => row.svs.some(id => svIds.has(id)));
    }

    const q = (this.searchCtrl.value ?? '').trim().toLowerCase();
    if (q) {
      rows = rows.filter(r => r.gene_name.toLowerCase().includes(q));
    }

    this.dataSource.data = rows;
    this.Variants = rows.length;
  }

  openAlignmentViewer(start: number, end: number, chr: string, svId?: number) {
    if (this.db.length == 0 || !start || !end ) {
      return;
    }

    const bp = (start + end) / 2;
    const payload : HceAlignmentViewRequest = {
      analysisId: this.analysis.AnalysisID,
      svId: svId,
      db: this.db,
      bp: bp,
      range: 3,
      chromosomeName: chr,
      alignmentScale: "scale1",
      annotationFile: this.selectedAnnotationFiles,
    };
    const target = 'walignmentview'+bp;
    this.hceService.openAlignmentViewer(payload, target);
  }

  /* utility */
  private chrTokenToNum(chr: string): number | undefined {
    if (!this.chrMap.has(chr)) {
      const entry = Object.entries(this.refNameMap).find(
        ([, value]) => value === chr
      );
      this.chrMap.set(chr, entry ? Number(entry[0]) : undefined);
    }
    return this.chrMap.get(chr);
  }

  private chrMap = new Map<string, number | undefined>();

  private compareChrStartEnd(a: RefGeneListData, b: RefGeneListData): number {
    const ca = this.chrTokenToNum(a.chr), cb = this.chrTokenToNum(b.chr);
    if (ca && cb && ca !== cb) return ca - cb;

    if (a.start !== b.start) return a.start - b.start;
    return a.end - b.end;
  }
}
